import React, { useState, useEffect } from "react";
import {
  FaCarAlt,
  FaWalking,
  FaTrain,
  FaExchangeAlt,
  FaMapMarkerAlt,
  FaMotorcycle,
  FaBicycle,
  FaLocationArrow,
  FaChevronRight,
  FaChevronLeft,
  FaRoute,
  FaGoogle,
} from "react-icons/fa";
import "../Map/Sidebar.css";
import RouteSteps from "./RouteSteps";

const Sidebar = ({
  origin,
  setOrigin,
  destination,
  setDestination,
  response,
  travelMode,
  changeMode,
  routeIndex,
  setRouteIndex,
  onSearch,
  errorMessage,
  setResponse,
  availableModes,
  isCalculating,

}) => {
  const [suggestions, setSuggestions] = useState({ field: null, data: [] });
  const [loading, setLoading] = useState(false);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [modeTimes, setModeTimes] = useState({});
  const [modeCalculating, setModeCalculating] = useState(false);

  // Google Places Autocomplete Service
  const fetchSuggestions = async (text, field) => {
    if (text.length < 3) {
      setSuggestions({ field: null, data: [] });
      return;
    }

    if (!window.google || !window.google.maps.places) {
      console.warn("Google Places API not loaded");
      return;
    }

    const autocompleteService = new window.google.maps.places.AutocompleteService();

    autocompleteService.getPlacePredictions(
      {
        input: text,
        componentRestrictions: { country: "in" }, 
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions({ field, data: predictions });
        } else {
          setSuggestions({ field, data: [] });
        }
      }
    );
  };

  // Helper function to format duration text to shorter format
  const formatDuration = (durationText) => {
    if (!durationText || durationText === "N/A") return durationText;
    
    return durationText
      .replace(/\s*hours?\s*/g, "h ")
      .replace(/\s*mins?\s*/g, "m")
      .replace(/\s*days?\s*/g, "d ")
      .replace(/\s*secs?\s*/g, "s")
      .trim();
  };

  const calculateModeTimes = async (originValue, destinationValue) => {
    if (!originValue || !destinationValue || !window.google) return;

    setModeCalculating(true);
    const modes = ["TRANSIT", "DRIVING", "TWO_WHEELER", "BICYCLING", "WALKING"];
    const times = {};

    const directionsService = new window.google.maps.DirectionsService();

    for (const mode of modes) {
      try {
        const request = {
          origin: originValue,
          destination: destinationValue,
          travelMode: mode === "TWO_WHEELER" ? "DRIVING" : mode,
          provideRouteAlternatives: false,
        };

        const result = await new Promise((resolve, reject) => {
          directionsService.route(request, (response, status) => {
            if (status === "OK") {
              resolve(response);
            } else {
              reject(status);
            }
          });
        });

        if (result && result.routes && result.routes.length > 0) {
          times[mode] = formatDuration(result.routes[0].legs[0].duration.text);
        } else {
          times[mode] = "N/A";
        }
      } catch (error) {
        times[mode] = "N/A";
      }
    }

    setModeTimes(times);
    setModeCalculating(false);
  };

  // Auto-calculate mode times when origin or destination changes
  useEffect(() => {
    if (origin?.value && destination && window.google) {
      const destValue = destination?.value || destination;
      const timeoutId = setTimeout(() => {
        calculateModeTimes(origin.value, destValue);
      }, 500); // Debounce for 500ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [origin?.value, destination]);
  
  const handleSelectSuggestion = (item, field) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    
    // Geocode the place_id to get LatLng
    geocoder.geocode({ placeId: item.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const latLng = { lat: location.lat(), lng: location.lng() };
        const placeName = item.description;

        if (field === "origin") {
          setOrigin({ label: placeName, value: latLng });
          
          // Calculate mode times if destination exists
          if (destination) {
            const destValue = destination?.value || destination;
            calculateModeTimes(latLng, destValue);
          }
          
          onSearch({
            origin: { label: placeName, value: latLng },
            destination,
          });
        } else {
          // Store destination as object with LatLng for consistency
          setDestination({ label: placeName, value: latLng });
          
          // Calculate mode times if origin exists
          if (origin && origin.value) {
            calculateModeTimes(origin.value, latLng);
          }
          
          onSearch({
            origin,
            destination: { label: placeName, value: latLng },
          });
        }
      } else {
        console.error("Geocoding failed:", status);
      }
    });

    setSuggestions({ field: null, data: [] });
  };

  const getGoogleTravelMode = (mode) => {
  switch (mode) {
    case "DRIVING":
      return "driving";
    case "WALKING":
      return "walking";
    case "BICYCLING":
      return "bicycling";
    case "TRANSIT":
      return "transit";
    case "TWO_WHEELER":
      return "driving"; 
    default:
      return "driving";
  }
};


 const getGoogleMapsLink = (route) => {
  if (!route) return "#";

  const leg = route.legs[0];

  const origin = `${leg.start_location.lat()},${leg.start_location.lng()}`;
  const destination = `${leg.end_location.lat()},${leg.end_location.lng()}`;

  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=${getGoogleTravelMode(travelMode)}`;
};



  const useCurrentLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        const locationValue = { lat: latitude, lng: longitude };
        setOrigin({
          label: "Your Location",
          value: locationValue,
        });

        setSuggestions({ field: null, data: [] });
        
        // Calculate mode times if destination exists
        if (destination) {
          calculateModeTimes(locationValue, destination);
        }
        
        onSearch(); // Route calculate
      },
      () => alert("Location permission denied"),
      { enableHighAccuracy: true },
    );
  };
  const handleReverse = () => {
    let newOrigin;
    let newDestination;

    // Origin becomes previous destination
    if (destination) {
      if (typeof destination === "string") {
        newOrigin = { label: destination, value: destination };
      } else if (destination?.value) {
        // Destination is object with LatLng
        newOrigin = {
          label: destination.label,
          value: destination.value,
        };
      }
    } else {
      newOrigin = { label: "", value: "" };
    }

    // Destination becomes previous origin
    if (origin && origin.value) {
      newDestination = {
        label: origin.label,
        value: origin.value,
      };
    } else {
      newDestination = { label: "", value: "" };
    }

    setOrigin(newOrigin);
    setDestination(newDestination);
    setRouteIndex(0);
    setResponse(null);

    // Calculate mode times for reversed route
    if (newOrigin.value && newDestination.value) {
      calculateModeTimes(newOrigin.value, newDestination.value);
    }

    onSearch({
      origin: newOrigin,
      destination: newDestination,
    });
  };
  const fastestDuration = response
    ? Math.min(...response.routes.map((r) => r.legs[0].duration.value))
    : null;

    
  return (
    <div style={{
      ...sidebarStyle,
      width: isCollapsed ? 60 : 350,
      transition: "width 0.3s ease",
    }}>
      {/* Collapse/Expand Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={toggleButtonStyle}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
      </button>

      {/* Collapsed State - Show only icon */}
      {isCollapsed ? (
        <div style={collapsedContentStyle}>
          <FaRoute style={{ fontSize: 24, color: "#3f51b5" }} />
          <span style={{ fontSize: 10, color: "#666", marginTop: 4 }}>Routes</span>
        </div>
      ) : (
        /* Expanded State - Full sidebar content */
        <>
      <div style={inputAreaStyle}>
        <div style={logoStyle}>
          <img
            src="./logo.png"
            alt="Staffing Connect"
            style={{
              width: 70,
              height: 70,
              marginRight: 8,
              objectFit: "contain",
            }}
          />
          <span style={{  color: "#000", marginRight: 5 }}>Staffing</span>
          <span style={{ color: "#1f3ce2" }}>Connect</span>
        </div>

        <div style={inputContainerStyle}>
          <div style={{ position: "relative", borderBottom: "1px solid #eee" }}>
            <label style={labelStyle}>
              <FaLocationArrow style={{ marginRight: 6, color: "#3f51b5" }} />
              Start Point
            </label>

            <input
              style={inputFieldStyle}
              value={origin?.label || ""}
              onChange={(e) => {
                const typedValue = e.target.value;
                setOrigin({ label: typedValue, value: typedValue });
                fetchSuggestions(typedValue, "origin");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                 
                  const newOrigin = e.target.value;
                  setOrigin({ label: newOrigin, value: newOrigin }); 
                  
            
                  if (destination) {
                    calculateModeTimes(newOrigin, destination);
                  }
                  
                  onSearch();
                  setSuggestions({ field: null, data: [] });
                }
              }}
            />

            {origin?.label !== "Your Location" && (
              <button
                onClick={useCurrentLocation}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11,
                  marginTop: 6,
                  background: "transparent",
                  border: "none",
                  color: "#3f51b5",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FaMapMarkerAlt />
                Use Current Location
              </button>
            )}

            {suggestions.field === "origin" && (
              <ul style={suggestionListStyle}>
                {suggestions.data.map((s, i) => (
                  <li
                    key={i}
                    style={suggestionItemStyle}
                    onMouseDown={() => handleSelectSuggestion(s, "origin")}
                  >
                    {s.description}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ position: "relative" }}>
            <label style={labelStyle}>
              <FaLocationArrow
                style={{
                  marginRight: 6,
                  color: "#d32f2f",
                  transform: "rotate(180deg)",
                }}
              />
              End Point
            </label>
            <input
              style={inputFieldStyle}
              value={destination?.label || destination || ""}
              onChange={(e) => {
                const typedValue = e.target.value;
                setDestination(typedValue);
                fetchSuggestions(typedValue, "destination");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newDestination = e.target.value;
                  setDestination(newDestination);
                  
                  // Calculate mode times if origin exists
                  if (origin && origin.value) {
                    calculateModeTimes(origin.value, newDestination);
                  }
                  
                  onSearch();
                  setSuggestions({ field: null, data: [] });
                }
              }}
              onBlur={() =>
                setTimeout(() => setSuggestions({ field: null, data: [] }), 200)
              }
            />

            {suggestions.field === "destination" && (
              <ul style={suggestionListStyle}>
                {suggestions.data.map((s, i) => (
                  <li
                    key={i}
                    style={suggestionItemStyle}
                    onMouseDown={() => handleSelectSuggestion(s, "destination")}
                  >
                    {s.description}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            className="reverse-float-btn"
            onClick={handleReverse}
            title="Reverse locations"
          >
            <FaExchangeAlt />
          </button>
        </div>

        <div style={modeGroupStyle}>
          {[
            { id: "TRANSIT", label: "Transit", icon: <FaTrain /> },
            { id: "DRIVING", label: "Drive", icon: <FaCarAlt /> },
            { id: "TWO_WHEELER", label: "Bike", icon: <FaMotorcycle /> },
            { id: "BICYCLING", label: "Cycle", icon: <FaBicycle /> },
            { id: "WALKING", label: "Walk", icon: <FaWalking /> },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => availableModes[m.id] && changeMode(m.id)}
              disabled={!availableModes[m.id]}
              title={
                availableModes[m.id]
                  ? m.label
                  : `${m.label} not available for this route`
              }
              style={{
                ...modeButtonStyle,
                backgroundColor: travelMode === m.id ? "#3f51b5" : "#fff",
                color: !availableModes[m.id]
                  ? "#bbb"
                  : travelMode === m.id
                    ? "#fff"
                    : "#000",
                opacity: !availableModes[m.id] ? 0.5 : 1,
                cursor: !availableModes[m.id] ? "not-allowed" : "pointer",
                flexDirection: "column",
                padding: "8px 4px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {m.icon} {m.label}
              </div>
              
             
              <div style={{
                fontSize: 9,
                marginTop: 4,
                fontWeight: 600,
                color: travelMode === m.id ? "#fff" : "#666",
              }}>
                {modeCalculating ? (
                  "..."
                ) : modeTimes[m.id] ? (
                  modeTimes[m.id]
                ) : (
                  <span style={{ visibility: "hidden" }}>-</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <button
          onClick={() => {
            setLoading(true);
            onSearch();
            setTimeout(() => setLoading(false), 1000);
          }}
          disabled={loading}
          style={searchButtonStyle}
        >
          {loading ? "Calculated... wait" : "Find Best Routes"}
        </button>
      </div>

      <div style={{ padding: "0 20px 20px" }}>
        {response &&
          [...response.routes] 
            .sort((a, b) => a.legs[0].duration.value - b.legs[0].duration.value)
            .map((route, index) => {
              const isFastest =
                route.legs[0].duration.value === fastestDuration;

              return (
                <div key={index}>
                  <div
                    onClick={() => setRouteIndex(index)}
                    style={{
                      ...routeCardStyle,
                      borderColor: isFastest
                        ? "#2e7d32"
                        : routeIndex === index
                          ? "#3f51b5"
                          : "#eee",
                      backgroundColor: isFastest ? "#f1f8e9" : "#fff",
                    }}
                  >
                   
                    {isFastest && (
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#2e7d32",
                          marginBottom: 6,
                        }}
                      >
                        ⚡ Fastest Route
                      </div>
                    )}

                    <div
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <div style={{ fontSize: 20, fontWeight: "bold" }}>
                        {route.legs[0].duration.text}
                      </div>

                      <div style={{ fontWeight: 600, color: "#3f51b5" }}>
                        {route.legs[0].distance.text}
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
                      via {route.summary || "Main Route"}
                    </div>

                    {/* Transit mode summary icons - only show when collapsed */}
                    {selectedRouteIndex !== index && travelMode === "TRANSIT" && route.legs[0].steps && (
                      <div style={transitSummaryStyle}>
                        {route.legs[0].steps
                          .filter(step => step.travel_mode === "TRANSIT")
                          .slice(0, 4)
                          .map((step, i) => (
                            <div
                              key={i}
                              style={{
                                ...transitChipStyle,
                                backgroundColor: step.transit?.line?.color || "#3f51b5",
                                color: step.transit?.line?.text_color || "#fff",
                              }}
                            >
                              {step.transit?.line?.short_name || step.transit?.line?.vehicle?.type || "Bus"}
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Open in Google Maps - inside each card */}
                    <a href={getGoogleMapsLink(route)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={googleMapsButtonStyle}
                    >
                      <FaGoogle size={15} />
                      Show in Google Maps
                    </a>

                    {/* Expand/Collapse Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRouteIndex(selectedRouteIndex === index ? null : index);
                      }}
                      style={{
                        ...expandButtonStyle,
                        background: selectedRouteIndex === index ? "#e3f2fd" : "#f0f4ff",
                        borderColor: selectedRouteIndex === index ? "#90caf9" : "#dbe3ff",
                        color: selectedRouteIndex === index ? "#1976d2" : "#3f51b5",
                      }}
                    >
                      {selectedRouteIndex === index ? (
                        <>
                          <FaChevronRight style={{ transform: "rotate(-90deg)", marginRight: 8, fontSize: 10 }} />
                          <span>Hide Details</span>
                        </>
                      ) : (
                        <>
                          <FaChevronRight style={{ transform: "rotate(90deg)", marginRight: 8, fontSize: 10 }} />
                          <span>View Details</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Step-by-step details (expanded) */}
                  {selectedRouteIndex === index && (
                    <RouteSteps
                      route={route}
                      travelMode={travelMode}
                      onClose={() => setSelectedRouteIndex(null)}
                    />
                  )}
                </div>
              );
            })}

        {isCalculating && (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13 }}>
            Calculating best route...
          </div>
        )}

        {!isCalculating && errorMessage && (
          <div
            style={{
              padding: 20,
              color: "#d32f2f",
              fontSize: 13,
              textAlign: "center",
            }}
          >
          {errorMessage}
          </div>
        )}
      </div>
    </>
    )}
    </div>
  );
};

const sidebarStyle = {
  position: "absolute",
  top: 20,
  left: 20,
  zIndex: 1000,
  width: 350,
  background: "#fff",
  borderRadius: 20,
  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  maxHeight: "85vh",
  overflowY: "auto",
};
const inputAreaStyle = { padding: 20 };
const logoStyle = {
  color: "#d32f2f",
  fontWeight: "bold",
  fontSize: 22,
  marginBottom: 15,
};
const inputContainerStyle = {
  border: "1px solid #eee",
  borderRadius: 12,
  padding: 8,
  background: "#fafafa",
  position: "relative",
};
const labelStyle = {
  fontSize: 9,
  color: "#999",
  fontWeight: "bold",
  textTransform: "uppercase",
};
const inputFieldStyle = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 14,
  padding: "6px 8px",
  background: "transparent",
};
const modeGroupStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)", 
  gap: 8,
  marginTop: 15,
};

const modeButtonStyle = {
  padding: "10px 0",
  borderRadius: 12,
  border: "1px solid #eee",
  fontSize: 11,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
};

const searchButtonStyle = {
  width: "100%",
  padding: 14,
  background: "#3f51b5",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  marginTop: 15,
  fontWeight: "bold",
  cursor: "pointer",
};
const routeCardStyle = {
  padding: 15,
  marginBottom: 12,
  borderRadius: 15,
  border: "2px solid #eee",
  cursor: "pointer",
};
const suggestionListStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "#fff",
  zIndex: 1100,
  listStyle: "none",
  padding: 5,
  margin: 0,
  border: "1px solid #ddd",
  borderRadius: 10,
};
const suggestionItemStyle = { padding: 10, fontSize: 12, cursor: "pointer" };

const transitSummaryStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 10,
};

const transitChipStyle = {
  padding: "4px 8px",
  borderRadius: 6,
  fontSize: 10,
  fontWeight: 600,
};

const toggleButtonStyle = {
  position: "absolute",
  top: 10,
  right: 10,
  width: 32,
  height: 32,
  borderRadius: "50%",
  border: "1px solid #eee",
  background: "#fff",
  color: "#3f51b5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 10,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "all 0.2s ease",
};

const collapsedContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: 60,
  height: 120,
};

const expandButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  marginTop: 12,
  padding: "10px 14px",
  background: "#f0f4ff",
  border: "1px solid #dbe3ff",
  borderRadius: 10,
  color: "#3f51b5",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const googleMapsButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 10,
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 500,
  color: "#1a73e8",
  background: "#e8f0fe",
  borderRadius: 6,
  textDecoration: "none",
  border: "none",
  cursor: "pointer",
};

export default Sidebar;
